/**
 * useEngagementContractDraft — Phase 6a.10 / Spec §21.7.5.
 *
 * Debounced auto-draft hook that owns the engagement-slot persistence
 * lifecycle for the contract entry page. Responsibilities:
 *
 *   1. Accept value/dirty notifications from `<ContractFormContainer>`
 *      (called from its `onValuesChange` prop).
 *   2. Debounce by 500 ms; on fire, load the engagement from
 *      localStorage, splice the slot at `idx`, mark `contractsDirty[idx]`,
 *      transition calculated → stale if the engagement was previously
 *      calculated (Six Golden Rule 5), and persist.
 *   3. Expose `flushSave()` so navigation handlers (Önceki / Sonraki /
 *      Listeyi Görüntüle) can force an immediate save before navigation.
 *
 * The hook is pure-async at the boundary; component callers simply wire
 * `handleValuesChange` to the form and `flushSave` to nav buttons.
 *
 * Storage I/O is funneled through the DI service (`useHistoryStorage`)
 * so tests can swap in an in-memory mock.
 */

import * as React from "react";

import { useHistoryStorage } from "@/services";
import {
  buildContractFormSchema,
  type ContractFormValues,
} from "@/features/contract-form/schemas/contractFormSchema";
import { preparePayload } from "@/features/contract-form/schemas/preparePayload";
import type { ContractInput, EngagementHistoryEntry } from "@/types";

import { transitionToStale } from "./engagement-state";

const DEBOUNCE_MS = 500;

export interface EngagementContractDraft {
  /**
   * Wire this to `<ContractFormContainer onValuesChange={...}>`. The
   * call is fired on every keystroke; the hook debounces internally.
   */
  handleValuesChange: (
    values: ContractFormValues,
    isDirty: boolean,
  ) => void;
  /**
   * Force an immediate save of any pending debounced change. Returns a
   * promise so navigation callers can `await` before redirecting.
   */
  flushSave: () => Promise<void>;
}

export function useEngagementContractDraft(
  engagementId: string,
  contractIdx: number,
): EngagementContractDraft {
  const historyStorage = useHistoryStorage();

  // Keep the latest pending values in a ref so flushSave can read the
  // current snapshot without depending on React state.
  const pendingRef = React.useRef<{
    values: ContractFormValues;
    isDirty: boolean;
  } | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = React.useRef({ engagementId, contractIdx });

  // Refresh the active id ref when navigation changes the slot.
  React.useEffect(() => {
    idRef.current = { engagementId, contractIdx };
  }, [engagementId, contractIdx]);

  /** Persists the current pendingRef.values into the engagement slot. */
  const persist = React.useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending) return;
    const { engagementId: activeId, contractIdx: activeIdx } = idRef.current;
    pendingRef.current = null;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const engagement = await historyStorage.getEngagement(activeId);
    if (!engagement) return; // engagement deleted mid-edit — drop the save

    // Frontend Finding U (Phase 6a.11/F-1): native `<input type="number">`
    // emits string values, react-hook-form's `getValues()` returns the
    // working state verbatim (NOT the Zod-coerced output), and `safeParse`
    // here is the only place that converts strings → numbers before the
    // contract is written to localStorage. Without this step the slot
    // looks `●` (because deriveContractStatus runs its own safeParse and
    // accepts the coercion) but the persisted payload has string-typed
    // numerics, so the eventual Calculate POST is rejected by backend
    // Zod (which does NOT coerce, per Backend Spec §20).
    //
    // On schema failure we still persist the raw values so a partial-slot
    // round-trip keeps the auditor's in-progress edits visible; the
    // Calculate button stays disabled in that state, so backend-typing
    // never sees malformed data.
    // Phase 6a.22 — `preparePayload` runs after Zod coercion to:
    //   1) sort `modifications` by date ascending (audit chronological)
    //   2) omit `modifications` entirely when the array is empty
    // Phase 6a.23 extends with the same treatment for `historicalRates`
    // and `paymentDateRates`. The fallback path on schema failure still
    // persists raw values (Frontend Finding U semantics) so partial
    // edits remain visible; the Calculate button stays disabled in that
    // state, so backend-typing never sees malformed data.
    //
    // Phase 6a.23 — build the schema with the engagement's functional
    // currency so a USD/EUR engagement validates FX contracts against
    // the right axis. Otherwise the static default-TRY schema would
    // misjudge every non-TRY engagement's contracts and force the
    // slot through the raw-values fallback (skipping preparePayload's
    // sort+omit transforms).
    const slotSchema = buildContractFormSchema({
      functionalCurrency: engagement.functionalCurrency,
    });
    const parsed = slotSchema.safeParse(pending.values);
    const slot: ContractInput = parsed.success
      ? preparePayload(parsed.data)
      : (pending.values as unknown as ContractInput);

    const nextContracts = [...engagement.contracts];
    // Ensure the slot exists (defensive — the page should already have
    // appended on navigation).
    while (nextContracts.length <= activeIdx) nextContracts.push(null);
    nextContracts[activeIdx] = slot;

    const nextDirty = [...(engagement.contractsDirty ?? [])];
    while (nextDirty.length <= activeIdx) nextDirty.push(false);
    if (pending.isDirty) nextDirty[activeIdx] = true;

    let updated: EngagementHistoryEntry = {
      ...engagement,
      contracts: nextContracts,
      contractsDirty: nextDirty,
      contractCount: nextContracts.length,
      updatedAt: new Date().toISOString(),
    };

    // Phase 6a.11 / Spec §21.7.3 v1.8 — auto-clear server-rejection on
    // edit. Any field change in a `server-rejected` slot must drop that
    // slot's entry from `lastCalculateError`; the engagement-level
    // banner and the slot's `✗` indicator will both auto-recover.
    if (pending.isDirty && updated.lastCalculateError?.[activeIdx]) {
      const nextLastErr = { ...updated.lastCalculateError };
      delete nextLastErr[activeIdx];
      updated = {
        ...updated,
        lastCalculateError:
          Object.keys(nextLastErr).length > 0 ? nextLastErr : undefined,
      };
    }

    // Six Golden Rule 5: any field change in a calculated engagement
    // → immediate stale transition. The helper is a no-op for draft
    // and already-stale engagements.
    if (pending.isDirty) {
      updated = transitionToStale(updated);
    }

    await historyStorage.saveEngagement(updated);
  }, [historyStorage]);

  const handleValuesChange = React.useCallback<
    EngagementContractDraft["handleValuesChange"]
  >(
    (values, isDirty) => {
      pendingRef.current = { values, isDirty };
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void persist();
      }, DEBOUNCE_MS);
    },
    [persist],
  );

  const flushSave = React.useCallback<
    EngagementContractDraft["flushSave"]
  >(async () => {
    if (pendingRef.current === null) return;
    await persist();
  }, [persist]);

  // Cancel any pending timer on unmount to avoid late writes for a
  // stale engagement/idx.
  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return { handleValuesChange, flushSave };
}
